import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { MwButton, MwInputText, MwSelector, MwSpinnerButton, NoDataFound } from '../ui';
import { useTranslation } from 'react-i18next';
import { useCurrent, useFetch } from '../../hooks';
import RequisitionsReportsList from './RequisitionsReportsList';
import MwCardWrapper from '../ui/MwCardWrapper';
import { printableDiv } from '../../utils/global';
import { FaRegFileExcel, FaPrint, FaFilePdf } from "react-icons/fa";
import handleConvertToPDF from '../../utils/handleConvertToPDF';
import usePost from '../../hooks/usePost';

const RequisitionsReports = () => {
  const divPrintRef = useRef();
  const [t] = useTranslation('global');
  const { currentLangId, currentTenantId, currentCompanyId, currentBranchId } = useCurrent();

  // تحديد الفترة تلقائيًا (بداية الشهر ونهايته)
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toLocaleDateString('en-CA');
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toLocaleDateString('en-CA');

  // حالات النموذج
  const [formStates, setFormStates] = useState({
    startDate: firstDay,
    endDate: lastDay,
  });
  const [fromWarehouse, setFromWarehouse] = useState("");
  const [reqType, setReqType] = useState("");
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isPrintMode, setIsPrintMode] = useState(false);

  // جلب بيانات المخازن (يظل كما هو)
  const warehousesUrl = `${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/warehouses/${currentTenantId}/${currentCompanyId}/${currentBranchId}`;
  const { data: warehousesData } = useFetch(warehousesUrl);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    if (warehousesData && warehousesData.data) {
      const formatted = warehousesData.data.map(warehouse => ({
        _id: warehouse.id,
        label: warehouse.name_ar || warehouse.name,
      }));
      setWarehouses([{ _id: "", label: t("reports.All") }, ...formatted]);
    }
  }, [warehousesData]);

  // خيارات نوع الإذن
  const reqTypeOptions = [
    { _id: "", label: t("reports.All") },
    { _id: 'PASH', label: t("reports.Permission_Receipt") },       // إذن صرف
    { _id: 'PULL', label: t("reports.Permission_Disbursement") },  //  إذن سحب
    { _id: 'TRANS', label: t("reports.Permission_Transfer") }       // إذن تحويل
  ];

  // استخدام الهوك الجديد لإجراء طلب POST
  const { data: filterDataResult, loading: filterLoading, postData } = usePost();

  // عند الضغط على زر "إعداد التقرير"
  const submitHandler = (e) => {
    e.preventDefault();
    // التحقق من الحقول الأساسية: الفترة والمخزن (نوع الإذن اختياري)
    if (!formStates.startDate || !formStates.endDate) return;
    const payload = {
      start_date: formStates.startDate,
      end_date: formStates.endDate,
      warehouse_id: fromWarehouse,
      req_type: reqType ? reqType : "", // إذا لم يتم اختيار أي نوع سيتم إرسال قيمة فارغة
      tenant_id: currentTenantId,
      company_id: currentCompanyId,
      branch_id: currentBranchId
    };
    console.log("payload",payload)
    postData(`${process.env.REACT_APP_INVENTORY_API_SERVER_BASE_URL}/requisitions/filter`, payload);
    setShouldFetch(true);
  };

  // دوال الطباعة والتحويل إلى PDF وتصدير Excel (تبقى نفس الوظائف)
  const printHandler = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      printableDiv(divPrintRef, currentLangId);
      setTimeout(() => {
        setIsPrintMode(false);
      }, 1000);
    }, 100);
  };

  const pdfHandler = () => {
    setIsPrintMode(true);
    setTimeout(() => {
      handleConvertToPDF({
        lang: 'ar',
        elementId: 'printableDiv',
        fileName: `تقرير_الأذونات.pdf`
      });
      setTimeout(() => {
        setIsPrintMode(false);
      }, 1000);
    }, 100);
  };

  const exportExcel = () => {
    if (filterDataResult?.data && filterDataResult.data.length > 0) {
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(filterDataResult.data);
      XLSX.utils.book_append_sheet(wb, ws, t("reports.Permissions_Report"));
      XLSX.writeFile(wb, `${t("reports.Permissions_Report")}.xlsx`);
    }
  };

  return (
    <div className='text-[#251658] h-[calc(100vh-50px)] custom-scrollbar'>
      <div id='header'>
        <p className='pb-3 text-sm'>{t("reports.Permissions_Report")}</p>
      </div>
       
      <MwCardWrapper>
        <form onSubmit={submitHandler} className='flex flex-col gap-5 p-5'>
          <div className="grid grid-cols-4 gap-5">
            {/* حقل الفترة من */}
            <div className="w-full">
              <label className="block text-start text-[#251658] mb-2 text-xs">
                {t("reports.Date_From")}
              </label>
              <div className="flex items-center">
                <MwInputText
                  inputType='date'
                  className="flex-grow"
                  value={formStates.startDate}
                  onChange={(e) => {
                    setFormStates({ ...formStates, startDate: e.target.value });
                  }}
                />
              </div>
            </div>

            {/* حقل الفترة إلى */}
            <div className="w-full">
              <label className="block text-start text-[#251658] mb-2 text-xs">
                {t("reports.Date_To")}
              </label>
              <div className="flex items-center">
                <MwInputText
                  inputType='date'
                  className="flex-grow"
                  value={formStates.endDate}
                  onChange={(e) => {
                    setFormStates({ ...formStates, endDate: e.target.value });
                  }}
                />
              </div>
            </div>

            {/* حقل اختيار المخزن */}
            <div className="w-full">
              <label className="block text-start text-[#251658] mb-2 text-xs">
                {t("reports.Choose_Warehouse")}
              </label>
              <MwSelector
                label=""
                _data={warehouses}
                dataType="labelValue"
                initalValue={t("reports.Choose_Warehouse")}
                selectedItem={warehouses.find(w => w._id === fromWarehouse)}
                setSelectedItem={(item) => {
                  setFromWarehouse(item._id);
                }}
                withSearch={false}
                withLabel={false}           
                     className="w-full text-center"
              />
            </div>

            {/* حقل اختيار نوع الإذن */}
            <div className="w-full">
              <label className="block text-start text-[#251658] mb-2 text-xs">
                {t("reports.Choose_Permission_Type")}
              </label>
              <MwSelector
                label=""
                _data={reqTypeOptions}
                dataType="labelValue"
                initalValue={t("reports.Choose_Permission_Type")}
                selectedItem={reqTypeOptions.find(r => r._id === reqType)}
                setSelectedItem={(item) => {
                  setReqType(item._id);
                }}
                withSearch={false}
              withLabel={false}
                className="w-full text-center"
              />
            </div>
          </div>

          <div className='pt-3 flex justify-end'>
            {!filterLoading ? (
              <MwButton 
                type='saveBtn' 
                disabled={!formStates.startDate || !formStates.endDate}
              >
                {t("reports.Prepare_Report")}
              </MwButton>
            ) : (
              <MwSpinnerButton/>
            )}
          </div>
        </form>
      </MwCardWrapper>

      {(filterLoading || (shouldFetch && filterDataResult)) && (
        <MwCardWrapper id='Result'>
          {filterDataResult?.data ? (
            filterDataResult.data.length > 0 ? (
              <div className='w-full'>
                <div className='flex justify-between'>
                  <div className='px-2 text-[#251658]'></div>
                  <div className='p-3 text-[#251658]'>
                    <ul className='flex gap-1'>
                      <li 
                        style={{ cursor: "pointer" }} 
                        onClick={exportExcel} 
                        className='px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold'
                      >
                        <FaRegFileExcel /> 
                        <span className='text-sm text-[#251658]'>{t("reports.Export_Excel")}</span>
                      </li>
                      <li 
                        style={{ cursor: "pointer" }} 
                        onClick={pdfHandler} 
                        className='px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold'
                      >
                        <FaFilePdf />
                        <span className='text-sm text-[#251658]'>{t("reports.Save_as_PDF")}</span>
                      </li>
                      <li 
                        style={{ cursor: "pointer" }} 
                        onClick={printHandler} 
                        className='px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold'
                      > 
                        <FaPrint />
                        <span className='text-sm text-[#251658]'>{t("reports.Print")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div
                  id='printableDiv'
                  ref={divPrintRef}
                  className={`text-[#251658] ${isPrintMode ? "" : "h-[calc(100vh-100px)] overflow-y-scroll custom-scrollbar"}`}
                >
                  <div className='text-[#251658] text-center'>
                    <div className='font-bold'>{t("reports.Permissions_Report_for_Period")}</div>
                    <div className='font-medium'>
                      <span>{t("reports.From")} {formStates.startDate}</span>
                      <span className="mx-2">{t("reports.To")} {formStates.endDate}</span>
                    </div>
                  </div>
                  {/* عرض القائمة باستخدام المكون الجديد وتمرير قائمة المخازن للعرض */}
                  {filterDataResult.data && (
                    <RequisitionsReportsList 
                      data={filterDataResult} 
                      warehouses={warehouses}
                    />
                  )}
                </div>
              </div>
            ) : (
              <NoDataFound msg={t("reports.No_Results")} />
            )
          ) : (
            <NoDataFound msg={t("reports.No_Results")} />
          )}
        </MwCardWrapper>
      )}
    </div>
  );
};

export default RequisitionsReports;
